// components/discovery/GameCard.jsx
import React, { useState } from 'react';
import { Heart, Users } from 'lucide-react';
import { useDiscovery } from '../../contexts/DiscoveryContext';
import GameDetailsModal from './GameDetailsModal';

const GameCard = ({ game }) => {
    const [showModal, setShowModal] = useState(false);
    const { settings } = useDiscovery();
    
    const { 
        name, 
        price, 
        wishlists, 
        followers, 
        genres, 
        tags,
        score,
        unreleased, 
        earlyAccess,
        publishers,
        developers,
        steamId
    } = game;

    // Filter only matched genres and tags
    const matchedGenres = genres.filter(genre => settings.scoring.genres[genre]);
    const matchedTags = tags.filter(tag => settings.scoring.tags[tag]);

    // Steam store URL
    const steamUrl = `https://store.steampowered.com/app/${steamId}`;

    const formatNumber = (num) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num;
    };

    // Get color based on score
    const getScoreColor = (score) => {
        if (score >= 15) return 'bg-emerald-100 text-emerald-700';
        if (score >= 10) return 'bg-green-100 text-green-700';
        return 'bg-blue-100 text-blue-700';
    };

    const classificationDisplay = game.publisherClassDisplay 
        || game.publisherClassification?.display
        || game.publisherClass
        || game.publisherComputedClass
        || 'Hobbyist';

    const classificationDelta = game.publisherClassification?.delta;
    const classificationBadgeClasses = classificationDelta === 'mismatch'
        ? 'bg-yellow-100 text-yellow-700'
        : 'bg-indigo-100 text-indigo-700';

    return (
        <>
            <div 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setShowModal(true)}
            >
                <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-semibold">{name}</h3>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${getScoreColor(score)}`}>
                                    Score: {score}
                                </span>
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${classificationBadgeClasses}`}>
                                    {classificationDisplay}
                                </span>
                                {earlyAccess && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                        Early Access
                                    </span>
                                )}
                            {unreleased && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    Unreleased
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center gap-2">
                            <Heart className="w-4 h-4 text-red-500" />
                            <span className="text-sm">{formatNumber(wishlists)} wishlists</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-500" />
                            <span className="text-sm">{formatNumber(followers)} followers</span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {matchedGenres.length > 0 && (
                            <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Matched Genres:</div>
                                <div className="flex flex-wrap gap-2">
                                    {matchedGenres.map(genre => (
                                        <span key={genre} 
                                              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                                            {genre}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {matchedTags.length > 0 && (
                            <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Matched Tags:</div>
                                <div className="flex flex-wrap gap-2">
                                    {matchedTags.map(tag => (
                                        <span key={tag}
                                              className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-4 border-t">
                        <div className="flex justify-between items-center">
                            <div className="text-sm text-gray-600">
                                {(() => {
                                    const publisherName = publishers?.[0]?.name || publishers?.[0];
                                    const developerName = developers?.[0]?.name || developers?.[0];
                                    
                                    // If both exist and are different, show both
                                    if (publisherName && developerName && publisherName !== developerName) {
                                        return (
                                            <div>
                                                <div className="font-medium">{publisherName}</div>
                                                <div className="text-xs text-gray-500">dev: {developerName}</div>
                                            </div>
                                        );
                                    }
                                    // If they're the same or only one exists, show just one
                                    else if (publisherName || developerName) {
                                        return (
                                            <div className="font-medium">
                                                {publisherName || developerName}
                                            </div>
                                        );
                                    }
                                    // Fallback
                                    else {
                                        return <div>Unknown Publisher</div>;
                                    }
                                })()}
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="font-semibold">
                                    {price === 0 ? 'Free' : `$${price.toFixed(2)}`}
                                </span>
                                <a 
                                    href={steamUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    Steam Page
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showModal && (
                <GameDetailsModal 
                    game={game} 
                    onClose={() => setShowModal(false)} 
                />
            )}
        </>
    );
};

export default GameCard;
